import ChevronDown16 from '@carbon/icons-react/es/chevron--down/16';
import { PopoverProps } from 'antd';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { OptionTypeBase } from 'react-select';
import styled from 'styled-components';
import { Button } from 'components/atoms/button';
import { OutsideClickHandler } from 'components/atoms/outside-click-handler';
import { Popover } from 'components/atoms/popover';
import { SelectProps } from 'components/atoms/select';
import { SingleLineEllipsis } from 'components/styles';
import { useTranslation } from 'hooks/use-translation';
import { isChildNode } from 'utils/is-child-node';
import { StyledTrigger } from './components';
import { Content } from './content';

interface DefaultTriggerProps {
  label: OptionTypeBase;
  onClick: () => void;
}

interface GroupedDropdownProps {
  title?: string | React.ReactNode;
  triggerComponent?: React.ReactElement;
  selectProps?: SelectProps<OptionTypeBase>;
  value?: OptionTypeBase;
  popoverProps?: Partial<PopoverProps>;
  customComponentClass?: string;
}

const GROUPED_DROPDOWN_HOSTED_COMPONENT_CLASS =
  'grouped-dropdown-hosted-component';

const StyledPopover = styled(Popover)`
  &.ant-popover {
    width: 296px;
    height: 448px;
  }
  .ant-popover-inner,
  .ant-popover-inner-content {
    height: 100%;
  }
  .ant-popover-inner-content,
  .ozone-select-container {
    display: flex;
    flex-flow: column;
  }
  .ozone-select-container {
    flex: 1;
    overflow: hidden;
  }
`;

export const TextWithEllipsis = styled.span`
  ${SingleLineEllipsis};
  max-width: 190px;
`;

const DefaultTrigger = ({ label, onClick }: DefaultTriggerProps) => {
  const defaultFallback = useTranslation('select_with_ellipsis');

  return (
    <StyledTrigger data-cy="select.fields" onClick={onClick}>
      <TextWithEllipsis>{label || defaultFallback}</TextWithEllipsis>
      <ChevronDown16 />
    </StyledTrigger>
  );
};

export const GroupedDropdown: FC<GroupedDropdownProps> = (props) => {
  const {
    title,
    triggerComponent,
    selectProps,
    value,
    popoverProps,
    customComponentClass,
  } = props;
  const [visible, setVisible] = useState(false);

  const GROUPED_DROPDOWN_CLASSES = useMemo(
    () => [GROUPED_DROPDOWN_HOSTED_COMPONENT_CLASS, customComponentClass ?? ''],
    [customComponentClass],
  );

  const _onChange = useCallback(
    (_value) => {
      selectProps?.onChange?.(_value);
      setVisible(false);
    },
    [selectProps],
  );

  const close = useCallback(() => {
    setVisible(false);
  }, []);

  const onOutsideClick = useCallback(() => {
    if (!visible) {
      return;
    }
    close();
  }, [close, visible]);

  const isChild = isChildNode(GROUPED_DROPDOWN_CLASSES);

  return (
    <OutsideClickHandler allowed={isChild} onOutsideClick={onOutsideClick}>
      <StyledPopover
        className={GROUPED_DROPDOWN_CLASSES.join(' ')}
        visible={visible}
        content={
          <Content
            title={title}
            selectProps={selectProps}
            onChange={_onChange}
            selected={value}
          />
        }
        trigger="click"
        placement="bottomLeft"
        {...popoverProps}
      >
        {triggerComponent ? (
          <Button
            data-cy="grouped_criteria.trigger"
            type="text"
            onClick={() => setVisible(true)}
          >
            {triggerComponent}
          </Button>
        ) : (
          <DefaultTrigger
            label={value?.label}
            onClick={() => setVisible(true)}
          />
        )}
      </StyledPopover>
    </OutsideClickHandler>
  );
};
